/* Repository path: frontend/src/app/App.tsx */

import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";
import { profileEditResultIfValid } from "../api/estimate";
import {
  answersToApi,
  apiToAnswers,
  calculatePreview,
  captureEmail,
  createQuestionnaireSession,
  editSessionProfile,
  fullAnswersToApi,
  refineQuestionnaire,
  updateQuestionnaire,
} from "../api/planner";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/Button";
import { LoadingState } from "../components/LoadingState";
import { ContentProvider } from "../content/ContentProvider";
import { RecoveryPage } from "../features/access/RecoveryPage";
import { VerifyPage } from "../features/access/VerifyPage";
import { ResultPage } from "../features/estimates/ResultPage";
import { RefinementPage, SavedRefinementPage } from "../features/estimates/RefinementPage";
import { ProfileEditPage, SavedProfileEditPage } from "../features/journey/ProfileEditPage";
import {
  clearInactive,
  firstNewlyActiveField,
  isActive,
  REVEAL_ANNOUNCEMENTS,
} from "../features/journey/profileConditionals";
import { QuestionnairePage } from "../features/journey/QuestionnairePage";
import { WelcomePage } from "../features/journey/WelcomePage";
import { SavedPlanPage } from "../features/plans/SavedPlanPage";
import { PrivacyPage } from "../features/privacy/PrivacyPage";
import { ReminderOffPage } from "../features/reminders/ReminderOffPage";
import type { Answers, EstimatePreview, PlanItem, ProfileEditResult } from "../types";
import { PRODUCT_NAME } from "../utils/productName";
import { useRouter } from "./router";

type Step = 1 | 2 | 3 | 4 | 5;

const DRAFT_KEY = "fast-profile-v2-draft";
const ACTIVE_PLAN_KEY = "fast-profile-v2-active-plan";

const EMPTY_ANSWERS: Answers = {
  municipalityContext: "",
  alreadyInNetherlands: "",
  arrivalDate: "",
  stayOverFourMonths: "",
  registrationStatus: "",
  residenceCategory: "",
  permitExpiryDate: "",
  permitExpiryUnknown: false,
  activities: [],
  recruitedBeforeArrival: "",
  businessStatus: "",
  housingType: "",
  studentAccommodationFacilities: "",
  householdType: "",
  insuranceType: "",
  assetIncomeFlags: [],
  vehicleType: "",
};

const STEP_FIELDS: Record<Step, (keyof Answers)[]> = {
  1: [
    "municipalityContext",
    "alreadyInNetherlands",
    "arrivalDate",
    "stayOverFourMonths",
    "registrationStatus",
  ],
  2: ["residenceCategory"],
  3: ["activities", "recruitedBeforeArrival", "businessStatus"],
  4: ["housingType", "studentAccommodationFacilities", "householdType"],
  5: ["insuranceType", "assetIncomeFlags", "vehicleType"],
};

const SERVER_STEPS: Record<string, Step> = {
  municipality_context: 1,
  already_in_netherlands: 1,
  arrival_date: 1,
  stay_over_four_months: 1,
  registration_status: 1,
  residence_category: 2,
  permit_expiry_date: 2,
  activities: 3,
  recruited_before_arrival: 3,
  business_status: 3,
  housing_type: 4,
  student_accommodation_facilities: 4,
  household_type: 4,
  insurance_type: 5,
  asset_income_flags: 5,
  vehicle_type: 5,
};

interface StoredDraft {
  sessionId: string;
  answers: Answers;
}

interface StoredActivePlan {
  sessionId: string;
}

function readDraft(): StoredDraft | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? "null") as unknown;
    if (!value || typeof value !== "object") return null;
    const draft = value as Partial<StoredDraft>;
    if (typeof draft.sessionId !== "string" || !draft.answers) return null;
    return { sessionId: draft.sessionId, answers: draft.answers };
  } catch {
    return null;
  }
}

function clearDraft() {
  sessionStorage.removeItem(DRAFT_KEY);
}

function persistDraft(sessionId: string, answers: Answers) {
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ sessionId, answers }));
}

function readActivePlan(): StoredActivePlan | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(ACTIVE_PLAN_KEY) ?? "null") as unknown;
    if (!value || typeof value !== "object") return null;
    const stored = value as Partial<StoredActivePlan>;
    if (typeof stored.sessionId !== "string" || !stored.sessionId) return null;
    return { sessionId: stored.sessionId };
  } catch {
    sessionStorage.removeItem(ACTIVE_PLAN_KEY);
    return null;
  }
}

function clearActivePlan() {
  sessionStorage.removeItem(ACTIVE_PLAN_KEY);
}

function persistActivePlan(sessionId: string) {
  if (sessionId) {
    sessionStorage.setItem(ACTIVE_PLAN_KEY, JSON.stringify({ sessionId }));
  }
}

function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    addEventListener("online", handleOnline);
    addEventListener("offline", handleOffline);
    return () => {
      removeEventListener("online", handleOnline);
      removeEventListener("offline", handleOffline);
    };
  }, []);
  return online;
}

function validateStep(step: Step, answers: Answers) {
  const errors: Record<string, string> = {};
  for (const field of STEP_FIELDS[step]) {
    const value = answers[field];
    const unanswered = Array.isArray(value) ? value.length === 0 : !value;
    if (isActive(field, answers) && unanswered) {
      errors[field] = "Complete this answer to continue.";
    }
  }
  if (step === 1 && !answers.arrivalDate) errors.arrivalDate = "Enter a valid date.";
  return errors;
}

const API_FIELD_NAMES: Record<string, keyof Answers> = {
  municipality_context: "municipalityContext",
  already_in_netherlands: "alreadyInNetherlands",
  arrival_date: "arrivalDate",
  stay_over_four_months: "stayOverFourMonths",
  registration_status: "registrationStatus",
  residence_category: "residenceCategory",
  permit_expiry_date: "permitExpiryDate",
  activities: "activities",
  recruited_before_arrival: "recruitedBeforeArrival",
  business_status: "businessStatus",
  housing_type: "housingType",
  student_accommodation_facilities: "studentAccommodationFacilities",
  household_type: "householdType",
  insurance_type: "insuranceType",
  asset_income_flags: "assetIncomeFlags",
  vehicle_type: "vehicleType",
};

const MULTI_VALUE_FIELDS = new Set<keyof Answers>(["activities", "assetIncomeFlags"]);

function apiErrorMessage(field: keyof Answers, code: string): string {
  if (code === "invalid_date" || field === "arrivalDate" || field === "permitExpiryDate") {
    return "Enter a valid date.";
  }
  if (code === "required" && !MULTI_VALUE_FIELDS.has(field)) return "Choose one answer.";
  if (code === "required" || code === "invalid_selection" || code === "exclusive_selection") {
    return "Choose one option, or select ‘I’m not sure’.";
  }
  return "Choose one answer.";
}

function mapApiErrors(error: ApiError): Record<string, string> {
  const mapped: Record<string, string> = {};
  for (const [field, code] of Object.entries(error.fieldErrors ?? {})) {
    const clientField = API_FIELD_NAMES[field];
    if (clientField) mapped[clientField] = apiErrorMessage(clientField, code);
  }
  return Object.keys(mapped).length ? mapped : { page: "We couldn’t save these answers. Try again." };
}

export function App() {
  const { route, navigate, justClosedItemCode, clearJustClosedItemCode } = useRouter();
  const online = useOnline();
  const [initialDraft] = useState(readDraft);
  const [initialActivePlan] = useState(readActivePlan);
  const resumeAttempted = useRef(false);
  const [answers, setAnswers] = useState<Answers>(initialDraft?.answers ?? EMPTY_ANSWERS);
  const [sessionId, setSessionId] = useState(initialDraft?.sessionId ?? initialActivePlan?.sessionId ?? "");
  const [preview, setPreview] = useState<EstimatePreview | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(() => Boolean(
    initialActivePlan
    && ["result", "refine-result", "edit-profile-result"].includes(route.name),
  ));
  const [emailPending, setEmailPending] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [refineCode, setRefineCode] = useState("");
  const [changeSummary, setChangeSummary] = useState<ProfileEditResult | null>(null);

  useEffect(() => {
    document.title = route.name === "questionnaire"
      ? "Your fast profile"
      : PRODUCT_NAME;
  }, [route.name]);

  useEffect(() => {
    if (sessionId) {
      if (route.name === "questionnaire") persistDraft(sessionId, answers);
    }
  }, [answers, route.name, sessionId]);

  useEffect(() => {
    if (preview && sessionId) {
      persistActivePlan(sessionId);
    }
  }, [preview, sessionId]);

  useEffect(() => {
    if (
      !initialActivePlan
      || preview
      || !["result", "refine-result", "edit-profile-result"].includes(route.name)
    ) return;
    calculatePreview(initialActivePlan.sessionId)
      .then((restored) => {
        setSessionId(initialActivePlan.sessionId);
        setPreview(restored);
      })
      .catch((error: unknown) => {
        clearActivePlan();
        setSessionId("");
        setErrors({
          page: error instanceof ApiError
            ? error.message
            : "We couldn’t restore your overview in this tab. Start a new check to generate it again.",
        });
      })
      .finally(() => setBusy(false));
  }, [initialActivePlan, preview, route.name]);

  useEffect(() => {
    if (resumeAttempted.current || route.name !== "questionnaire" || !initialDraft) return;
    resumeAttempted.current = true;
    updateQuestionnaire(initialDraft.sessionId, {})
      .then((response) => {
        const step = SERVER_STEPS[response.next];
        if (step) navigate(`/questionnaire/${step}`, true);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.code === "session_not_found") {
          clearDraft();
          setSessionId("");
          setAnswers(EMPTY_ANSWERS);
          navigate("/", true);
        } else {
          setErrors(error instanceof ApiError ? mapApiErrors(error) : {
            page: "We couldn’t restore these answers. Refresh and try again.",
          });
        }
      });
  }, [initialDraft, navigate, route.name]);

  const start = () => {
    clearDraft();
    clearActivePlan();
    setAnswers(EMPTY_ANSWERS);
    setSessionId("");
    setPreview(null);
    setErrors({});
    navigate("/questionnaire/1");
  };

  const changeAnswer = <K extends keyof Answers>(key: K, value: Answers[K]) => {
    setAnswers((current) => {
      const provisional = { ...current, [key]: value };
      const next = clearInactive(provisional);
      const revealed = firstNewlyActiveField(current, next);
      if (revealed) {
        setAnnouncement(`Additional question shown: ${REVEAL_ANNOUNCEMENTS[revealed] ?? ""}`);
      }
      return next;
    });
    setErrors((current) => {
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const submit = async (step: Step) => {
    const localErrors = validateStep(step, answers);
    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      let id = sessionId;
      if (!id) {
        id = (await createQuestionnaireSession()).session_id;
        setSessionId(id);
      }
      const nextAnswers = answers;
      persistDraft(id, nextAnswers);
      const response = await updateQuestionnaire(id, answersToApi(nextAnswers, step));
      persistDraft(id, nextAnswers);
      if (response.complete && step === 5) {
        setPreview(await calculatePreview(id));
        clearDraft();
        setAnnouncement("Overview ready");
        navigate("/result");
      } else {
        const next = SERVER_STEPS[response.next] ?? Math.min(5, step + 1);
        navigate(`/questionnaire/${next}`);
      }
    } catch (error) {
      setErrors(
        error instanceof ApiError
          ? mapApiErrors(error)
          : { page: "We couldn’t save these answers. Try again." },
      );
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (email: string, optIn: boolean) => {
    if (!preview) return;
    setBusy(true);
    try {
      await captureEmail(preview.plan_preview_id, email, optIn);
      setEmailPending(true);
    } catch (error) {
      setErrors({
        page: error instanceof ApiError
          ? error.message
          : "We couldn’t send the link. Try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const openRefinement = (item?: PlanItem) => {
    setRefineCode(item?.missing_input?.code ?? "");
    setErrors({});
    navigate("/result/refine");
  };

  const openProfileEditor = () => {
    setErrors({});
    navigate("/result/edit-profile");
  };

  // W4-07: edits the Fast Profile behind the current preview. Appends to
  // the *same* plan `preview.plan_preview_id` names — never routes through
  // `updateQuestionnaire`/`calculatePreview`, which is exactly the path
  // that would otherwise create a second plan. See
  // docs/architecture/features/W4-07.md §1.
  const saveProfileEdit = async (profile: Answers) => {
    if (!preview) return;
    setBusy(true);
    setErrors({});
    try {
      const result = await editSessionProfile(
        sessionId,
        preview.input_version,
        fullAnswersToApi(profile),
      );
      setPreview(result);
      setChangeSummary(profileEditResultIfValid(result.profile_edit_result));
      setAnnouncement("Plan updated");
      navigate("/result");
    } catch (error) {
      if (error instanceof ApiError && error.code === "stale_input_version") {
        setErrors({
          page: "Your plan changed in another tab or window. Reload it to see the current answers before editing.",
        });
      } else {
        setErrors(
          error instanceof ApiError
            ? mapApiErrors(error)
            : { page: "We couldn’t save your answers. Try again." },
        );
      }
    } finally {
      setBusy(false);
    }
  };

  let content: React.ReactNode;
  if (route.name === "welcome") {
    content = <WelcomePage onStart={start} />;
  } else if (route.name === "questionnaire") {
    content = (
      <QuestionnairePage
        step={route.step ?? 1}
        answers={answers}
        errors={errors}
        busy={busy}
        online={online}
        onChange={changeAnswer}
        onSubmit={submit}
        onAnnounce={setAnnouncement}
        onBack={() => route.step === 1
          ? navigate("/")
          : navigate(`/questionnaire/${(route.step ?? 2) - 1}`)}
      />
    );
  } else if (route.name === "result") {
    content = (
      <ResultPage
        preview={preview}
        emailPending={emailPending}
        busy={busy}
        online={online}
        errors={errors}
        onEdit={openProfileEditor}
        onStart={start}
        onEmail={submitEmail}
        onPrivacy={() => navigate("/privacy")}
        onImprove={openRefinement}
        detailItemCode={route.itemCode}
        focusItemCode={justClosedItemCode}
        onFocusHandled={clearJustClosedItemCode}
        changeSummary={changeSummary}
        onChangeSummaryHandled={() => setChangeSummary(null)}
        announce={setAnnouncement}
      />
    );
  } else if (route.name === "edit-profile-result") {
    content = preview ? (
      <ProfileEditPage
        original={apiToAnswers(preview.profile ?? {})}
        busy={busy}
        online={online}
        errors={errors}
        onSave={saveProfileEdit}
        onCancel={() => navigate("/result")}
        onAnnounce={setAnnouncement}
      />
    ) : (
      <section className="empty-state page--narrow">
        <p className="eyebrow">No active questionnaire</p>
        <h1>Start the check to see an estimate</h1>
        <p>Your overview could not be restored in this tab. Start a new check to generate it again.</p>
        <Button onClick={start}>Start the check</Button>
      </section>
    );
  } else if (route.name === "refine-result" && preview) {
    content = (
      <RefinementPage
        estimate={preview}
        requestedCode={refineCode}
        online={online}
        announce={setAnnouncement}
        onBack={() => navigate("/result")}
        onEstimateChange={setPreview}
        submit={(inputVersion, inputCode, value, key) =>
          refineQuestionnaire(sessionId, inputVersion, inputCode, value, key)
        }
      />
    );
  } else if (route.name === "verify") {
    content = <VerifyPage navigate={navigate} announce={setAnnouncement} />;
  } else if (route.name === "recovery") {
    content = <RecoveryPage online={online} announce={setAnnouncement} />;
  } else if (route.name === "reminders-off") {
    content = <ReminderOffPage navigate={navigate} />;
  } else if (route.name === "plan" && route.publicId) {
    content = (
      <SavedPlanPage
        key={route.publicId}
        publicId={route.publicId}
        online={online}
        announce={setAnnouncement}
        navigate={navigate}
        onImprove={(item) => {
          setRefineCode(item?.missing_input?.code ?? "");
          navigate(`/plans/${route.publicId}/refine`);
        }}
        onEdit={() => navigate(`/plans/${route.publicId}/edit-profile`)}
        detailItemCode={route.itemCode}
        focusItemCode={justClosedItemCode}
        onFocusHandled={clearJustClosedItemCode}
        changeSummary={changeSummary}
        onChangeSummaryHandled={() => setChangeSummary(null)}
      />
    );
  } else if (route.name === "edit-profile-plan" && route.publicId) {
    content = (
      <SavedProfileEditPage
        publicId={route.publicId}
        online={online}
        navigate={navigate}
        announce={setAnnouncement}
        onSaved={(result) => {
          setChangeSummary(profileEditResultIfValid(result.profile_edit_result));
          navigate(`/plans/${route.publicId}`);
        }}
      />
    );
  } else if (route.name === "refine-plan" && route.publicId) {
    content = <SavedRefinementPage publicId={route.publicId} requestedCode={refineCode} online={online} navigate={navigate} announce={setAnnouncement} />;
  } else if (route.name === "privacy") {
    content = <PrivacyPage onBack={() => navigate("/")} />;
  } else {
    content = (
      <section className="empty-state page--narrow">
        <h1>Start the check to see your overview</h1>
        <Button onClick={start}>Start the check</Button>
      </section>
    );
  }

  return (
    <ContentProvider>
      <AppShell online={online} announcement={announcement} onPrivacy={() => navigate("/privacy")}>
        {busy && ["result", "refine-result", "edit-profile-result"].includes(route.name) && !preview ? <LoadingState /> : content}
      </AppShell>
    </ContentProvider>
  );
}





